import axios from 'axios';
import log from 'lib/logger';
import { subscribeToNotifications } from '../channels/notification_channel';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = ['notificationList', 'notificationIndicator', 'notificationCountLabel'];

  // The server renders the count the badge starts with; broadcasts and marking the list read change it.
  static values = { unread: Number };

  src = null;

  // A broadcast arrived while the list was closed: it is fetched again the next time it opens.
  stale = false;

  listVisible = false;

  initialize() {
    this.observer = new IntersectionObserver(this.listVisibilityChanged.bind(this), { threshold: [1] });
  }

  connect() {
    this.src = this.notificationListTarget.getAttribute('data-lazy-load-src-value');
    this.unsubscribe = subscribeToNotifications();
  }

  disconnect() {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  notificationListTargetConnected(target) {
    this.observer.observe(target);
  }

  notificationListTargetDisconnected(target) {
    this.observer?.unobserve(target);
  }

  // A broadcast with the new unread count (channels/notification_channel.js). Every open tab gets one per
  // notification, so the list is fetched only if it is open; otherwise it waits for the next time it opens.
  refreshMenu(event) {
    if (event.detail?.unreadCount === undefined) return;

    this.unreadValue = event.detail.unreadCount;
    if (this.listVisible) {
      this.updateLazyLoadSrc();
    } else if (this.listLoaded) {
      // A list never opened yet loads fresh on its first open (lazy-load), so only a loaded one goes stale.
      this.stale = true;
    }
  }

  unreadValueChanged() {
    const count = this.unreadValue;
    this.notificationIndicatorTarget.classList.toggle('d-none', count <= 0);
    this.notificationIndicatorTarget.classList.toggle('d-inline-block', count > 0);
    this.notificationCountLabelTarget.textContent = count > 9 ? '9+' : `${count}`;
  }

  // Private

  async listVisibilityChanged(entries) {
    const lastEntry = entries.slice(-1)[0];
    this.listVisible = !!lastEntry?.isIntersecting;
    if (!this.listVisible) return;

    if (this.stale) {
      this.stale = false;
      this.updateLazyLoadSrc();
    }
    // Opening the menu reads everything in it; with nothing unread there is nothing to tell the server.
    if (this.unreadValue <= 0) return;

    try {
      await axios.post('/lazy_load/notifications/mark_all_read');
      this.unreadValue = 0;
    } catch (error) {
      log.error(error);
    }
  }

  // The fetched list (layouts/menus/_notification_items) carries the count it was rendered with.
  get listLoaded() {
    return !!this.notificationListTarget.querySelector('[data-notification-count]');
  }

  updateLazyLoadSrc() {
    if (!this.src) return;

    const url = new URL(this.src, window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('refetchTime', new Date().getTime());
    url.search = params.toString();

    this.notificationListTarget.setAttribute('data-lazy-load-src-value', url.toString());
  }
}
